import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { Event } from '../types/index.ts';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, BarChart, QrCode, Calendar, Users, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export default function ManageEvents() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('published');

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const data = await api.getMyEvents();
      setEvents(data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (eventId: string) => {
    if (!confirm('Are you sure you want to delete this event?')) return;

    try {
      await api.deleteEvent(eventId);
      toast.success('Event deleted successfully');
      setEvents(events.filter(e => e.event_id !== eventId));
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete event');
    }
  };

  const publishedEvents = events.filter(e => e.status === 'published');
  const draftEvents = events.filter(e => e.status === 'draft');
  const pastEvents = events.filter(e => new Date(e.date) < new Date());

  const renderEvents = (eventList: Event[]) => {
    if (eventList.length === 0) {
      return (
        <Card className="glass border-white/10 p-12 text-center">
          <Calendar className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg mb-4">No events found</p>
          <Button onClick={() => navigate('/create-event')} className="shadow-glow">
            <Plus className="h-4 w-4 mr-2" />
            Create Event
          </Button>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {eventList.map((event) => (
          <EventCard
            key={event.event_id}
            event={event}
            onEdit={() => navigate(`/edit-event/${event.event_id}`)}
            onDelete={() => handleDelete(event.event_id)}
            onViewAnalytics={() => navigate(`/host-analytics/${event.event_id}`)}
            onScanTickets={() => navigate(`/ticket-scanner/${event.event_id}`)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
            Manage Events
          </h1>
          <p className="text-lg text-gray-400 mt-2">
            Create and manage your events
          </p>
        </div>
        <Button onClick={() => navigate('/create-event')} className="shadow-glow">
          <Plus className="h-4 w-4 mr-2" />
          Create Event
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="glass border-white/10">
          <TabsTrigger value="published">
            Published ({publishedEvents.length})
          </TabsTrigger>
          <TabsTrigger value="drafts">
            Drafts ({draftEvents.length})
          </TabsTrigger>
          <TabsTrigger value="past">
            Past ({pastEvents.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="published" className="mt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <Card key={i} className="glass border-white/10 h-32 animate-pulse" />
              ))}
            </div>
          ) : (
            renderEvents(publishedEvents)
          )}
        </TabsContent>

        <TabsContent value="drafts" className="mt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <Card key={i} className="glass border-white/10 h-32 animate-pulse" />
              ))}
            </div>
          ) : (
            renderEvents(draftEvents)
          )}
        </TabsContent>

        <TabsContent value="past" className="mt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <Card key={i} className="glass border-white/10 h-32 animate-pulse" />
              ))}
            </div>
          ) : (
            renderEvents(pastEvents)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EventCard({
  event,
  onEdit,
  onDelete,
  onViewAnalytics,
  onScanTickets,
}: {
  event: Event;
  onEdit: () => void;
  onDelete: () => void;
  onViewAnalytics: () => void;
  onScanTickets: () => void;
}) {
  const isPast = new Date(event.date) < new Date();
  const ticketsSold = event.tickets_sold || 0;
  const capacity = event.capacity || 0;
  const revenue = ticketsSold * (event.price || 0);
  const soldPercentage = capacity > 0 ? (ticketsSold / capacity) * 100 : 0;

  return (
    <Card className="glass border-white/10 overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start space-x-4">
          {/* Event Image */}
          <div className="h-24 w-24 rounded-lg overflow-hidden bg-background-subtle flex-shrink-0">
            {event.poster_url || event.image ? (
              <img
                src={event.poster_url || event.image}
                alt={event.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Calendar className="h-10 w-10 text-gray-600" />
              </div>
            )}
          </div>

          {/* Event Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <h3 className="font-heading font-bold text-xl truncate">{event.title}</h3>
                  {event.status === 'draft' && (
                    <Badge variant="outline">Draft</Badge>
                  )}
                  {isPast && (
                    <Badge variant="secondary">Past</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-400">{event.artist}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="space-y-1">
                <div className="flex items-center space-x-2 text-gray-400">
                  <Calendar className="h-4 w-4" />
                  <span className="text-xs">Date</span>
                </div>
                <p className="text-sm font-medium">
                  {format(new Date(event.date), 'MMM dd, yyyy')}
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center space-x-2 text-gray-400">
                  <Users className="h-4 w-4" />
                  <span className="text-xs">Tickets Sold</span>
                </div>
                <p className="text-sm font-medium">
                  {ticketsSold} / {capacity}
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center space-x-2 text-gray-400">
                  <DollarSign className="h-4 w-4" />
                  <span className="text-xs">Revenue</span>
                </div>
                <p className="text-sm font-medium">${revenue.toFixed(2)}</p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center space-x-2 text-gray-400">
                  <BarChart className="h-4 w-4" />
                  <span className="text-xs">Sold</span>
                </div>
                <p className="text-sm font-medium">{soldPercentage.toFixed(0)}%</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="h-2 bg-background-subtle rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary to-secondary"
                  style={{ width: `${soldPercentage}%` }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button onClick={onEdit} size="sm" variant="outline" className="glass-hover">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button onClick={onViewAnalytics} size="sm" variant="outline" className="glass-hover">
                <BarChart className="h-4 w-4 mr-2" />
                Analytics
              </Button>
              {!isPast && (
                <Button onClick={onScanTickets} size="sm" variant="outline" className="glass-hover">
                  <QrCode className="h-4 w-4 mr-2" />
                  Scan Tickets
                </Button>
              )}
              <Button onClick={onDelete} size="sm" variant="ghost" className="text-destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
